import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getAllLabEquipmentRequest, resetGetAllLabEquipment, getByIdLabEquipmentRequest, resetGetByIdLabEquipment, updateLabEquipmentRequest, resetUpdateLabEquipment, createLabEquipmentRequest, resetCreateLabEquipment, addMaintenanceRequest, resetAddMaintenance, addCalibrationRequest, resetAddCalibration, addDowntimeRequest, resetAddDowntime } from '../../../../redux/modules/laboratory/labEquipment/slice';
import { getLabDepartmentRequest } from '../../../../redux/modules/laboratory/labDepartment/slice';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, IconButton, Tooltip, Tabs, Tab, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import SpeedIcon from '@mui/icons-material/Speed';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import LabInventory from '../inventory';

const statusColors = {
    operational: 'success',
    maintenance: 'warning',
    out_of_service: 'error',
    decommissioned: 'default',
};

export default function LabEquipment() {
    const dispatch = useDispatch();
    const { getAllRes, getByIdRes, createRes, updateRes, addMaintenanceRes, addCalibrationRes, addDowntimeRes, loading } = useSelector(state => state.labEquipment);
    const { getRes: deptRes } = useSelector(state => state.labDepartment);

    const [pageTab, setPageTab] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [detailDialog, setDetailDialog] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [detailTab, setDetailTab] = useState(0);

    // Form dialogs
    const [createDialog, setCreateDialog] = useState(false);
    const [maintenanceDialog, setMaintenanceDialog] = useState(false);
    const [calibrationDialog, setCalibrationDialog] = useState(false);
    const [downtimeDialog, setDowntimeDialog] = useState(false);

    // Form states
    const [form, setForm] = useState({ name: '', manufacturer: '', model: '', serialNumber: '', assetTag: '', departmentId: '', location: '', installationDate: '', warrantyExpiry: '', nextMaintenanceDue: '', nextCalibrationDue: '' });
    const [maintenanceForm, setMaintenanceForm] = useState({ type: 'preventive', description: '', nextDueDate: '', cost: '', notes: '' });
    const [calibrationForm, setCalibrationForm] = useState({ nextCalibrationDue: '', passed: true, notes: '' });
    const [downtimeForm, setDowntimeForm] = useState({ reason: '', impact: '' });

    useEffect(() => {
        dispatch(setMainHeading("Equipment & Inventory"));
        dispatch(getAllLabEquipmentRequest({}));
        dispatch(getLabDepartmentRequest());
    }, [dispatch]);

    useEffect(() => {
        if (createRes?.success) {
            toast.success("Equipment created");
            setCreateDialog(false);
            setForm({ name: '', manufacturer: '', model: '', serialNumber: '', assetTag: '', departmentId: '', location: '', installationDate: '', warrantyExpiry: '', nextMaintenanceDue: '', nextCalibrationDue: '' });
            dispatch(resetCreateLabEquipment());
            dispatch(getAllLabEquipmentRequest({}));
        } else if (createRes && !createRes.success) {
            toast.error(createRes.message || "Failed to create equipment");
            dispatch(resetCreateLabEquipment());
        }
    }, [createRes, dispatch]);

    useEffect(() => {
        if (updateRes?.success) {
            toast.success("Equipment updated");
            dispatch(resetUpdateLabEquipment());
            dispatch(getAllLabEquipmentRequest({}));
        }
    }, [updateRes, dispatch]);

    useEffect(() => {
        if (addMaintenanceRes?.success) {
            toast.success("Maintenance log added");
            setMaintenanceDialog(false);
            setMaintenanceForm({ type: 'preventive', description: '', nextDueDate: '', cost: '', notes: '' });
            dispatch(resetAddMaintenance());
            if (selectedEquipment) dispatch(getByIdLabEquipmentRequest(selectedEquipment._id));
        }
    }, [addMaintenanceRes, dispatch, selectedEquipment]);

    useEffect(() => {
        if (addCalibrationRes?.success) {
            toast.success("Calibration record added");
            setCalibrationDialog(false);
            setCalibrationForm({ nextCalibrationDue: '', passed: true, notes: '' });
            dispatch(resetAddCalibration());
            if (selectedEquipment) dispatch(getByIdLabEquipmentRequest(selectedEquipment._id));
        }
    }, [addCalibrationRes, dispatch, selectedEquipment]);

    useEffect(() => {
        if (addDowntimeRes?.success) {
            toast.success("Downtime logged");
            setDowntimeDialog(false);
            setDowntimeForm({ reason: '', impact: '' });
            dispatch(resetAddDowntime());
            if (selectedEquipment) dispatch(getByIdLabEquipmentRequest(selectedEquipment._id));
            dispatch(getAllLabEquipmentRequest({}));
        }
    }, [addDowntimeRes, dispatch, selectedEquipment]);

    useEffect(() => {
        if (getByIdRes?.success) {
            setSelectedEquipment(getByIdRes.data);
        }
    }, [getByIdRes]);

    const equipment = getAllRes?.data || [];
    const departments = deptRes?.data || [];
    const filtered = statusFilter ? equipment.filter(e => e.status === statusFilter) : equipment;

    const handleViewDetail = (eq) => {
        setSelectedEquipment(eq);
        setDetailTab(0);
        dispatch(getByIdLabEquipmentRequest(eq._id));
        setDetailDialog(true);
    };

    const handleCreate = () => {
        dispatch(createLabEquipmentRequest(form));
    };

    return (
        <Box>
            <Tabs value={pageTab} onChange={(_, v) => setPageTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Equipment" />
                <Tab label="Inventory" />
            </Tabs>

            {pageTab === 1 && <LabInventory />}

            {pageTab === 0 && (<>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status Filter</InputLabel>
                    <Select value={statusFilter} label="Status Filter" onChange={e => { setStatusFilter(e.target.value); dispatch(getAllLabEquipmentRequest(e.target.value ? { status: e.target.value } : {})); }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="operational">Operational</MenuItem>
                        <MenuItem value="maintenance">Maintenance</MenuItem>
                        <MenuItem value="out_of_service">Out of Service</MenuItem>
                        <MenuItem value="decommissioned">Decommissioned</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}>Add Equipment</Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Manufacturer / Model</TableCell>
                            <TableCell>Serial #</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Next Maintenance</TableCell>
                            <TableCell>Next Calibration</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map(eq => (
                            <TableRow key={eq._id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{eq.name}</TableCell>
                                <TableCell>{[eq.manufacturer, eq.model].filter(Boolean).join(' / ')}</TableCell>
                                <TableCell>{eq.serialNumber || '-'}</TableCell>
                                <TableCell>{eq.departmentId?.name || '-'}</TableCell>
                                <TableCell>{eq.location || '-'}</TableCell>
                                <TableCell><Chip label={eq.status?.replace('_', ' ')} color={statusColors[eq.status] || 'default'} size="small" /></TableCell>
                                <TableCell>{eq.nextMaintenanceDue ? new Date(eq.nextMaintenanceDue).toLocaleDateString() : '-'}</TableCell>
                                <TableCell>{eq.nextCalibrationDue ? new Date(eq.nextCalibrationDue).toLocaleDateString() : '-'}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="View Details">
                                        <IconButton size="small" onClick={() => handleViewDetail(eq)}><VisibilityIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Log Maintenance">
                                        <IconButton size="small" color="primary" onClick={() => { setSelectedEquipment(eq); setMaintenanceDialog(true); }}><BuildIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Log Calibration">
                                        <IconButton size="small" color="info" onClick={() => { setSelectedEquipment(eq); setCalibrationDialog(true); }}><SpeedIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Log Downtime">
                                        <IconButton size="small" color="error" onClick={() => { setSelectedEquipment(eq); setDowntimeDialog(true); }}><ReportProblemIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filtered.length === 0 && (
                            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No equipment found</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Equipment Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Equipment</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField label="Name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} size="small" />
                    <TextField label="Manufacturer" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} size="small" />
                    <TextField label="Model" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} size="small" />
                    <TextField label="Serial Number" value={form.serialNumber} onChange={e => setForm({ ...form, serialNumber: e.target.value })} size="small" />
                    <TextField label="Asset Tag" value={form.assetTag} onChange={e => setForm({ ...form, assetTag: e.target.value })} size="small" />
                    <FormControl size="small">
                        <InputLabel>Department</InputLabel>
                        <Select value={form.departmentId} label="Department" onChange={e => setForm({ ...form, departmentId: e.target.value })}>
                            {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField label="Location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} size="small" />
                    <TextField label="Installation Date" type="date" InputLabelProps={{ shrink: true }} value={form.installationDate} onChange={e => setForm({ ...form, installationDate: e.target.value })} size="small" />
                    <TextField label="Warranty Expiry" type="date" InputLabelProps={{ shrink: true }} value={form.warrantyExpiry} onChange={e => setForm({ ...form, warrantyExpiry: e.target.value })} size="small" />
                    <TextField label="Next Maintenance Due" type="date" InputLabelProps={{ shrink: true }} value={form.nextMaintenanceDue} onChange={e => setForm({ ...form, nextMaintenanceDue: e.target.value })} size="small" />
                    <TextField label="Next Calibration Due" type="date" InputLabelProps={{ shrink: true }} value={form.nextCalibrationDue} onChange={e => setForm({ ...form, nextCalibrationDue: e.target.value })} size="small" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!form.name || loading}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Equipment Detail Dialog */}
            <Dialog open={detailDialog} onClose={() => { setDetailDialog(false); dispatch(resetGetByIdLabEquipment()); }} maxWidth="md" fullWidth>
                <DialogTitle>{selectedEquipment?.name || 'Equipment Details'}</DialogTitle>
                <DialogContent>
                    {selectedEquipment && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip label={selectedEquipment.status?.replace('_', ' ')} color={statusColors[selectedEquipment.status] || 'default'} />
                                {selectedEquipment.manufacturer && <Typography variant="body2" color="text.secondary">{selectedEquipment.manufacturer} {selectedEquipment.model}</Typography>}
                                {selectedEquipment.serialNumber && <Typography variant="body2" color="text.secondary">S/N: {selectedEquipment.serialNumber}</Typography>}
                            </Box>

                            <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
                                <Tab label={`Maintenance (${selectedEquipment.maintenanceLogs?.length || 0})`} />
                                <Tab label={`Calibration (${selectedEquipment.calibrationRecords?.length || 0})`} />
                                <Tab label={`Downtime (${selectedEquipment.downtimeLogs?.length || 0})`} />
                            </Tabs>

                            {detailTab === 0 && (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell>Performed By</TableCell>
                                                <TableCell>Cost</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(selectedEquipment.maintenanceLogs || []).map((log, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{new Date(log.performedAt).toLocaleDateString()}</TableCell>
                                                    <TableCell><Chip label={log.type} size="small" /></TableCell>
                                                    <TableCell>{log.description}</TableCell>
                                                    <TableCell>{log.performedBy ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() : '-'}</TableCell>
                                                    <TableCell>{log.cost ? `$${log.cost}` : '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(!selectedEquipment.maintenanceLogs || selectedEquipment.maintenanceLogs.length === 0) && (
                                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No maintenance logs</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {detailTab === 1 && (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Result</TableCell>
                                                <TableCell>Calibrated By</TableCell>
                                                <TableCell>Next Due</TableCell>
                                                <TableCell>Notes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(selectedEquipment.calibrationRecords || []).map((rec, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{new Date(rec.calibratedAt).toLocaleDateString()}</TableCell>
                                                    <TableCell><Chip label={rec.passed ? 'Passed' : 'Failed'} color={rec.passed ? 'success' : 'error'} size="small" /></TableCell>
                                                    <TableCell>{rec.calibratedBy ? `${rec.calibratedBy.firstName || ''} ${rec.calibratedBy.lastName || ''}`.trim() : '-'}</TableCell>
                                                    <TableCell>{rec.nextCalibrationDue ? new Date(rec.nextCalibrationDue).toLocaleDateString() : '-'}</TableCell>
                                                    <TableCell>{rec.notes || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(!selectedEquipment.calibrationRecords || selectedEquipment.calibrationRecords.length === 0) && (
                                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No calibration records</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {detailTab === 2 && (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Started</TableCell>
                                                <TableCell>Ended</TableCell>
                                                <TableCell>Reason</TableCell>
                                                <TableCell>Impact</TableCell>
                                                <TableCell>Reported By</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(selectedEquipment.downtimeLogs || []).map((log, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{new Date(log.startedAt).toLocaleDateString()}</TableCell>
                                                    <TableCell>{log.endedAt ? new Date(log.endedAt).toLocaleDateString() : <Chip label="Ongoing" color="error" size="small" />}</TableCell>
                                                    <TableCell>{log.reason}</TableCell>
                                                    <TableCell>{log.impact || '-'}</TableCell>
                                                    <TableCell>{log.reportedBy ? `${log.reportedBy.firstName || ''} ${log.reportedBy.lastName || ''}`.trim() : '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(!selectedEquipment.downtimeLogs || selectedEquipment.downtimeLogs.length === 0) && (
                                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No downtime logs</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                    {!selectedEquipment && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDetailDialog(false); dispatch(resetGetByIdLabEquipment()); }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Maintenance Dialog */}
            <Dialog open={maintenanceDialog} onClose={() => setMaintenanceDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Log Maintenance - {selectedEquipment?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <FormControl size="small">
                        <InputLabel>Type</InputLabel>
                        <Select value={maintenanceForm.type} label="Type" onChange={e => setMaintenanceForm({ ...maintenanceForm, type: e.target.value })}>
                            <MenuItem value="preventive">Preventive</MenuItem>
                            <MenuItem value="corrective">Corrective</MenuItem>
                            <MenuItem value="emergency">Emergency</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Description" required multiline rows={2} value={maintenanceForm.description} onChange={e => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })} size="small" />
                    <TextField label="Next Due Date" type="date" InputLabelProps={{ shrink: true }} value={maintenanceForm.nextDueDate} onChange={e => setMaintenanceForm({ ...maintenanceForm, nextDueDate: e.target.value })} size="small" />
                    <TextField label="Cost" type="number" value={maintenanceForm.cost} onChange={e => setMaintenanceForm({ ...maintenanceForm, cost: e.target.value })} size="small" />
                    <TextField label="Notes" multiline rows={2} value={maintenanceForm.notes} onChange={e => setMaintenanceForm({ ...maintenanceForm, notes: e.target.value })} size="small" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMaintenanceDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => dispatch(addMaintenanceRequest({ id: selectedEquipment._id, ...maintenanceForm, cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : undefined }))} disabled={!maintenanceForm.description || loading}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Calibration Dialog */}
            <Dialog open={calibrationDialog} onClose={() => setCalibrationDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Log Calibration - {selectedEquipment?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <FormControl size="small">
                        <InputLabel>Result</InputLabel>
                        <Select value={calibrationForm.passed} label="Result" onChange={e => setCalibrationForm({ ...calibrationForm, passed: e.target.value })}>
                            <MenuItem value={true}>Passed</MenuItem>
                            <MenuItem value={false}>Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Next Calibration Due" type="date" InputLabelProps={{ shrink: true }} value={calibrationForm.nextCalibrationDue} onChange={e => setCalibrationForm({ ...calibrationForm, nextCalibrationDue: e.target.value })} size="small" />
                    <TextField label="Notes" multiline rows={2} value={calibrationForm.notes} onChange={e => setCalibrationForm({ ...calibrationForm, notes: e.target.value })} size="small" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCalibrationDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => dispatch(addCalibrationRequest({ id: selectedEquipment._id, ...calibrationForm }))} disabled={loading}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Downtime Dialog */}
            <Dialog open={downtimeDialog} onClose={() => setDowntimeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Log Downtime - {selectedEquipment?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField label="Reason" required multiline rows={2} value={downtimeForm.reason} onChange={e => setDowntimeForm({ ...downtimeForm, reason: e.target.value })} size="small" />
                    <TextField label="Impact" multiline rows={2} value={downtimeForm.impact} onChange={e => setDowntimeForm({ ...downtimeForm, impact: e.target.value })} size="small" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDowntimeDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={() => dispatch(addDowntimeRequest({ id: selectedEquipment._id, ...downtimeForm }))} disabled={!downtimeForm.reason || loading}>Log Downtime</Button>
                </DialogActions>
            </Dialog>
            </>)}
        </Box>
    );
}
